import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RotateCw } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

interface ObjectTransformControlsProps {
  position: { x: number; y: number }; // Position en % (0-100)
  scale: number; // Scale de l'objet (0.3 - 3)
  rotation: number; // Rotation en degrés (0-360)
  baseSize: number; // Taille de base en pixels (pour calculer les dimensions du cadre)
  onPositionChange: (position: { x: number; y: number }) => void;
  onScaleChange: (scale: number) => void;
  onRotationChange: (rotation: number) => void;
}

// Helpers pour obtenir les coordonnées (souris ou touch)
const getClientCoords = (e: MouseEvent | TouchEvent): { clientX: number; clientY: number } => {
  if ('touches' in e && e.touches.length > 0) {
    return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
  }
  if ('clientX' in e) {
    return { clientX: e.clientX, clientY: e.clientY };
  }
  return { clientX: 0, clientY: 0 };
};

const ObjectTransformControls: React.FC<ObjectTransformControlsProps> = ({
  position,
  scale,
  rotation,
  baseSize,
  onPositionChange,
  onScaleChange,
  onRotationChange
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState<'tl' | 'tr' | 'bl' | 'br' | null>(null);
  const [isRotating, setIsRotating] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, posX: 0, posY: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, scale: 0 });
  const [rotationStart, setRotationStart] = useState({ centerX: 0, centerY: 0, startAngle: 0, rotation: 0 });

  // Taille du cadre basée sur la taille de base et le scale
  const boxSize = baseSize * scale;

  // Drag du cadre pour déplacer l'objet
  const handleDragStart = (clientX: number, clientY: number) => {
    setIsDragging(true);
    setDragStart({
      x: clientX,
      y: clientY,
      posX: position.x,
      posY: position.y
    });
  };

  const handleBorderMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    handleDragStart(e.clientX, e.clientY);
  };

  const handleBorderTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (e.touches.length === 1) {
      handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  // Resize handles (garde le ratio)
  const handleResizeStart = (corner: 'tl' | 'tr' | 'bl' | 'br', clientX: number, clientY: number) => {
    setIsResizing(corner);
    setResizeStart({
      x: clientX,
      y: clientY,
      scale: scale
    });
  };

  const handleResizeMouseDown = (corner: 'tl' | 'tr' | 'bl' | 'br', e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    handleResizeStart(corner, e.clientX, e.clientY);
  };

  const handleResizeTouchStart = (corner: 'tl' | 'tr' | 'bl' | 'br', e: React.TouchEvent) => {
    e.stopPropagation();
    if (e.touches.length === 1) {
      handleResizeStart(corner, e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  // Rotation handle
  const handleRotationStart = (clientX: number, clientY: number) => {
    // Calculer la position du centre de l'objet à l'écran
    const centerX = (window.innerWidth * position.x) / 100;
    const centerY = (window.innerHeight * position.y) / 100;

    // Calculer l'angle initial entre le point et le centre
    const startAngle = Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI);

    setIsRotating(true);
    setRotationStart({
      centerX,
      centerY,
      startAngle,
      rotation: rotation
    });
  };

  const handleRotationMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    handleRotationStart(e.clientX, e.clientY);
  };

  const handleRotationTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (e.touches.length === 1) {
      handleRotationStart(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  useEffect(() => {
    const handleMove = (clientX: number, clientY: number, shiftKey: boolean = false) => {
      // Drag - déplacement en %
      if (isDragging) {
        const deltaX = clientX - dragStart.x;
        const deltaY = clientY - dragStart.y;

        // Convertir les pixels en pourcentage
        const deltaXPercent = (deltaX / window.innerWidth) * 100;
        const deltaYPercent = (deltaY / window.innerHeight) * 100;

        const newX = Math.max(0, Math.min(100, dragStart.posX + deltaXPercent));
        const newY = Math.max(0, Math.min(100, dragStart.posY + deltaYPercent));

        onPositionChange({ x: newX, y: newY });
      }

      // Resize - scale proportionnel en tenant compte de la rotation
      if (isResizing) {
        const deltaX = clientX - resizeStart.x;
        const deltaY = clientY - resizeStart.y;

        // Convertir les deltas dans le système de coordonnées de l'objet roté
        const radians = (rotation * Math.PI) / 180;
        const cos = Math.cos(-radians); // -radians pour rotation inverse
        const sin = Math.sin(-radians);

        const rotatedDeltaX = deltaX * cos - deltaY * sin;
        const rotatedDeltaY = deltaX * sin + deltaY * cos;

        // Calculer la distance dans le système de coordonnées roté
        const distance = Math.max(Math.abs(rotatedDeltaX), Math.abs(rotatedDeltaY));

        // Déterminer si on agrandit ou réduit selon le coin (dans le système roté)
        let direction = 1;
        if (isResizing === 'tl') {
          direction = (rotatedDeltaX < 0 && rotatedDeltaY < 0) ? 1 : -1;
        } else if (isResizing === 'tr') {
          direction = (rotatedDeltaX > 0 && rotatedDeltaY < 0) ? 1 : -1;
        } else if (isResizing === 'bl') {
          direction = (rotatedDeltaX < 0 && rotatedDeltaY > 0) ? 1 : -1;
        } else if (isResizing === 'br') {
          direction = (rotatedDeltaX > 0 && rotatedDeltaY > 0) ? 1 : -1;
        }

        // Calculer le nouveau scale (sensibilité de 0.005 par pixel)
        const scaleDelta = (distance * direction * 0.005);
        let newScale = resizeStart.scale + scaleDelta;

        // Limiter le scale entre 0.3 et 3
        newScale = Math.max(0.3, Math.min(3, newScale));

        onScaleChange(newScale);
      }

      // Rotation - suivre le mouvement circulaire
      if (isRotating) {
        // Calculer l'angle actuel entre le point et le centre
        const currentAngle = Math.atan2(
          clientY - rotationStart.centerY,
          clientX - rotationStart.centerX
        ) * (180 / Math.PI);

        // Calculer la différence d'angle
        let angleDelta = currentAngle - rotationStart.startAngle;

        // Calculer la nouvelle rotation
        let degrees = rotationStart.rotation + angleDelta;

        // Snap to 15° with Shift (souris uniquement)
        if (shiftKey) {
          degrees = Math.round(degrees / 15) * 15;
        }

        // Normaliser entre 0 et 360
        degrees = ((degrees % 360) + 360) % 360;

        onRotationChange(degrees);
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      handleMove(e.clientX, e.clientY, e.shiftKey);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        e.preventDefault(); // Empêcher le scroll
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const handleEnd = () => {
      setIsDragging(false);
      setIsResizing(null);
      setIsRotating(false);
    };

    if (isDragging || isResizing || isRotating) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
      document.addEventListener('touchcancel', handleEnd);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleEnd);
        document.removeEventListener('touchcancel', handleEnd);
      };
    }
  }, [isDragging, isResizing, isRotating, dragStart, resizeStart, rotationStart, position, scale, rotation, onPositionChange, onScaleChange, onRotationChange]);

  return (
    <div
      className="absolute"
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        width: `${boxSize}px`,
        height: `${boxSize}px`,
        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        transformOrigin: 'center center',
        zIndex: 1000,
        pointerEvents: 'none' // Le container ne bloque pas les events
      }}
    >
      {/* Bandes du cadre (subtiles, juste visuelles) */}
      <div className="absolute left-0 right-0 bg-blue-500 bg-opacity-10 pointer-events-none" style={{ top: -4, height: 4 }} />
      <div className="absolute top-0 bottom-0 bg-blue-500 bg-opacity-10 pointer-events-none" style={{ right: -4, width: 4 }} />
      <div className="absolute left-0 right-0 bg-blue-500 bg-opacity-10 pointer-events-none" style={{ bottom: -4, height: 4 }} />
      <div className="absolute top-0 bottom-0 bg-blue-500 bg-opacity-10 pointer-events-none" style={{ left: -4, width: 4 }} />

      {/* Zone centrale pour drag (toute l'image) */}
      <div
        className="absolute inset-0 pointer-events-auto cursor-move hover:bg-blue-100 hover:bg-opacity-20 transition-all"
        onMouseDown={handleBorderMouseDown}
        onTouchStart={handleBorderTouchStart}
        title="Glisser pour déplacer"
        style={{ boxSizing: 'border-box', touchAction: 'none' }}
      />

      {/* Border visuel */}
      <div
        className="absolute inset-0 border-2 border-blue-500 pointer-events-none"
        style={{ boxSizing: 'border-box' }}
      />

      {/* Resize handles (4 coins) - garde le ratio - Plus grands sur mobile */}
      <div
        className="absolute w-5 h-5 md:w-3 md:h-3 bg-white border-2 border-blue-500 rounded-full cursor-pointer pointer-events-auto hover:scale-125 transition-transform"
        style={{ left: -10, top: -10, touchAction: 'none' }}
        onMouseDown={(e) => handleResizeMouseDown('tl', e)}
        onTouchStart={(e) => handleResizeTouchStart('tl', e)}
        title="Redimensionner"
      />
      <div
        className="absolute w-5 h-5 md:w-3 md:h-3 bg-white border-2 border-blue-500 rounded-full cursor-pointer pointer-events-auto hover:scale-125 transition-transform"
        style={{ right: -10, top: -10, touchAction: 'none' }}
        onMouseDown={(e) => handleResizeMouseDown('tr', e)}
        onTouchStart={(e) => handleResizeTouchStart('tr', e)}
        title="Redimensionner"
      />
      <div
        className="absolute w-5 h-5 md:w-3 md:h-3 bg-white border-2 border-blue-500 rounded-full cursor-pointer pointer-events-auto hover:scale-125 transition-transform"
        style={{ left: -10, bottom: -10, touchAction: 'none' }}
        onMouseDown={(e) => handleResizeMouseDown('bl', e)}
        onTouchStart={(e) => handleResizeTouchStart('bl', e)}
        title="Redimensionner"
      />
      <div
        className="absolute w-5 h-5 md:w-3 md:h-3 bg-white border-2 border-blue-500 rounded-full cursor-pointer pointer-events-auto hover:scale-125 transition-transform"
        style={{ right: -10, bottom: -10, touchAction: 'none' }}
        onMouseDown={(e) => handleResizeMouseDown('br', e)}
        onTouchStart={(e) => handleResizeTouchStart('br', e)}
        title="Redimensionner"
      />

      {/* Rotation handle (cercle en haut) - Plus grand sur mobile */}
      <div
        className="absolute w-7 h-7 md:w-5 md:h-5 bg-green-500 border-2 border-white rounded-full cursor-pointer pointer-events-auto hover:scale-125 transition-transform flex items-center justify-center"
        style={{
          left: '50%',
          top: -32,
          transform: 'translateX(-50%)',
          touchAction: 'none'
        }}
        onMouseDown={handleRotationMouseDown}
        onTouchStart={handleRotationTouchStart}
        title="Rotation"
      >
        <RotateCw size={14} className="text-white" />
      </div>

      {/* Ligne entre rotation handle et bloc */}
      <div
        className="absolute w-0.5 bg-blue-500 pointer-events-none"
        style={{
          left: '50%',
          top: -28,
          height: 24,
          transform: 'translateX(-50%)'
        }}
      />
    </div>
  );
};

export default ObjectTransformControls;
